import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Form,
  DatePicker,
  Input,
  Button,
  message,
  Alert,
  Checkbox,
  Typography,
  Tag,
  Descriptions,
} from 'antd';
import {
  CalendarOutlined,
  WarningOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';

dayjs.locale('fr');

const { Text } = Typography;
const { TextArea } = Input;

// Sous-ensemble des champs de Company nécessaires à la révision d'échéance
interface Company {
  id: number;
  name: string;
  email: string | null;
  status: string;
  trial_ends_at: string | null;
  licence_expire_on: string | null;
  plan_name: string | null;
  subscription_status: 'active' | 'expired' | 'trial' | 'suspended';
  days_remaining: number | null;
}

interface SubscriptionEditExpiryModalProps {
  visible: boolean;
  company: Company | null;
  onSuccess: () => void;
  onCancel: () => void;
}

// Statuts qui bloquent l'accès de l'entreprise (cf. CLAUDE.md - login gating)
const BLOCKING_STATUSES = ['suspended', 'inactive', 'disabled', 'terminated'];

const SubscriptionEditExpiryModal: React.FC<SubscriptionEditExpiryModalProps> = ({
  visible,
  company,
  onSuccess,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [newDate, setNewDate] = useState<dayjs.Dayjs | null>(null);
  const [reactivate, setReactivate] = useState(false);

  // Champ cible : licence si présente, sinon date de fin d'essai
  const targetField: 'licence_expire_on' | 'trial_ends_at' = useMemo(() => {
    if (!company) return 'licence_expire_on';
    if (company.licence_expire_on) return 'licence_expire_on';
    if (company.trial_ends_at) return 'trial_ends_at';
    return 'licence_expire_on';
  }, [company]);

  const currentDateRaw = company
    ? targetField === 'licence_expire_on'
      ? company.licence_expire_on
      : company.trial_ends_at
    : null;

  // Réinitialiser à l'ouverture
  useEffect(() => {
    if (visible && company) {
      const initial = currentDateRaw ? dayjs(currentDateRaw) : null;
      setNewDate(initial);
      setReactivate(false);
      form.resetFields();
      form.setFieldsValue({ new_date: initial, reason: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, company]);

  if (!company) return null;

  const isBlockingStatus = BLOCKING_STATUSES.includes(
    (company.status || '').toLowerCase()
  );
  const isPastDate = newDate ? newDate.endOf('day').isBefore(dayjs()) : false;
  const fieldLabel =
    targetField === 'licence_expire_on'
      ? "Date d'expiration de la licence"
      : "Date de fin d'essai";

  const handleSubmit = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }

    if (!newDate) {
      message.error('Veuillez choisir une nouvelle date');
      return;
    }

    const reason: string = form.getFieldValue('reason');

    const payload: Record<string, any> = {
      revision_reason: reason,
    };

    if (targetField === 'licence_expire_on') {
      // Format date simple : valide ISO8601 et accepté par MySQL (colonne date/datetime)
      payload.licence_expire_on = newDate.format('YYYY-MM-DD');
    } else {
      // Fin d'essai : fin de journée pour rester valide jusqu'au soir du jour choisi
      payload.trial_ends_at = newDate.endOf('day').format('YYYY-MM-DDTHH:mm:ss');
    }

    if (reactivate && isBlockingStatus) {
      payload.status = 'active';
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/subscription/companies/${company.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        message.success("Échéance révisée avec succès");
        onSuccess();
      } else {
        message.error(data.message || 'Erreur lors de la révision de l\'échéance');
      }
    } catch (error) {
      console.error('Erreur révision échéance:', error);
      message.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <div className="flex items-center">
          <CalendarOutlined className="mr-2 text-primary" />
          Réviser l'échéance de la souscription
        </div>
      }
      visible={visible}
      onCancel={onCancel}
      width={620}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={loading}>
          Annuler
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
          icon={<CalendarOutlined />}
        >
          Enregistrer la révision
        </Button>,
      ]}
    >
      {/* Rappel : cette opération est une correction administrative, pas un renouvellement */}
      <Alert
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        className="mb-4"
        message="Correction administrative"
        description="Cette action ajuste directement la date d'échéance sans générer de facture ni de paiement. Pour un renouvellement payant, utilisez l'action « Renouveler »."
      />

      <Descriptions
        size="small"
        column={1}
        bordered
        className="mb-4"
      >
        <Descriptions.Item label="Entreprise">
          <strong>{company.name}</strong>
        </Descriptions.Item>
        <Descriptions.Item label="Plan">
          <Tag color="blue">{company.plan_name || 'Aucun'}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Statut souscription">
          <Tag
            color={
              company.subscription_status === 'active'
                ? 'green'
                : company.subscription_status === 'trial'
                ? 'orange'
                : 'red'
            }
          >
            {company.subscription_status}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Échéance actuelle">
          {currentDateRaw ? (
            <Text>{dayjs(currentDateRaw).format('DD/MM/YYYY')}</Text>
          ) : (
            <Text type="secondary">Non définie</Text>
          )}
        </Descriptions.Item>
      </Descriptions>

      <Form form={form} layout="vertical">
        <Form.Item
          name="new_date"
          label={`Nouvelle ${fieldLabel.toLowerCase()}`}
          rules={[{ required: true, message: 'Veuillez choisir une date' }]}
        >
          <DatePicker
            className="w-full"
            format="DD/MM/YYYY"
            value={newDate}
            onChange={(date) => setNewDate(date)}
            placeholder="Sélectionner une date"
          />
        </Form.Item>

        <Form.Item
          name="reason"
          label="Motif de la révision"
          rules={[
            { required: true, message: 'Le motif est obligatoire pour la traçabilité' },
            { max: 500, message: '500 caractères maximum' },
          ]}
        >
          <TextArea
            rows={3}
            maxLength={500}
            showCount
            placeholder="Ex : geste commercial, correction d'une erreur de saisie, alignement de date de facturation..."
          />
        </Form.Item>

        {isBlockingStatus && (
          <Form.Item className="mb-2">
            <Checkbox
              checked={reactivate}
              onChange={(e) => setReactivate(e.target.checked)}
            >
              Réactiver l'entreprise (statut « active »)
            </Checkbox>
            <div className="text-xs text-gray-500 mt-1">
              L'entreprise est actuellement « {company.status} » : modifier la date
              seule ne rétablit pas l'accès au compte.
            </div>
          </Form.Item>
        )}
      </Form>

      {isPastDate && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          className="mt-2"
          message="Date dans le passé"
          description="La date choisie est antérieure à aujourd'hui : l'accès du client sera immédiatement bloqué (échéance expirée)."
        />
      )}
    </Modal>
  );
};

export default SubscriptionEditExpiryModal;
